/**
 * Black Friday → Brands — /black-friday/:year
 *
 * The season's register: one row per brand with status, owner, the six
 * standard asks, the terms in one line and the brand's promo dates. Replaces
 * the yearly "Black Friday Campaigns" Google Sheet. Clicking a row opens the
 * brand's terms page.
 */
import React, { useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, IconButton } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CommitInput } from '../../components/ui/CommitInput';
import { Icon } from '../../components/ui/Icon';
import { useToast } from '../../components/ui/Toast';
import { useBrands } from '../../lib/api/brands';
import { useCampaigns } from '../../lib/api/campaigns';
import {
  ASKS,
  BF_SECTIONS,
  BFStatus,
  BrandTerms,
  STATUS_LABEL,
  STATUS_ORDER,
  TermsPatch,
  fmtDay,
  fmtRange,
  fmtEurShort,
  rememberBFYear,
  rememberedBFYear,
  fmtPct,
  num,
  selloutLine,
  sellinLine,
  siteDiscountLine,
  useBFOwners,
  useBFPriority,
  useBFSeason,
  useBFSeasons,
  useBFTerms,
  useSeasonMutations,
  useTermsMutations,
} from '../../lib/api/blackFriday';
import { BF_STYLES } from './styles';

export const BrandDot = ({ name, logo, color }: { name: string; logo?: string; color?: string }) => (
  <span className="brand-dot" style={{ background: logo ? '#fff' : color || 'var(--ink)', border: logo ? '1px solid var(--border)' : 0 }}>
    {logo ? <img src={logo} alt="" /> : name.slice(0, 1).toUpperCase()}
  </span>
);

export const StatusPill = ({ status }: { status: BFStatus }) => (
  <span className={`pill st-${status}`}>{STATUS_LABEL[status]}</span>
);

/** /black-friday → the newest season (or next year's if none yet). */
/** /black-friday → the year you were last looking at in the Black Friday
 *  section (register or calendar), else the newest season. */
export const BFRedirect = () => {
  const seasons = useBFSeasons();
  if (seasons.isLoading) return null;
  const year = rememberedBFYear() ?? seasons.data?.[0]?.year ?? new Date().getFullYear();
  return <Navigate to={`/black-friday/${year}`} replace />;
};


/** The deal at a glance: three chips in the section colours — shopper
 *  discount, stock order, top-up. Only structured facts, never note text;
 *  a part that isn't agreed yet is a greyed chip. Details on hover. */
const DealChips = ({ t }: { t: BrandTerms }) => {
  const so = num(t.discount_max_pct) !== null ? fmtPct(t.discount_max_pct)
    : num(t.discount_recommended_pct) !== null ? fmtPct(t.discount_recommended_pct)
      : t.participates === 'no' ? 'no' : '';
  const si = [
    num(t.extra_discount_pct) !== null ? `+${fmtPct(t.extra_discount_pct)}` : '',
    num(t.minimum_order_net) !== null ? `min ${fmtEurShort(t.minimum_order_net)}` : '',
  ].filter(Boolean).join(' · ') || (num(t.order_amount) !== null ? `ordered ${fmtEurShort(t.order_amount)}` : '');
  const tu = t.topup_allowed === 'yes' ? `top-up${t.topup_deadline ? ` by ${fmtDay(t.topup_deadline)}` : ''}`
    : t.topup_allowed === 'no' ? 'no top-up' : t.topup_allowed === 'conditional' ? 'top-up?' : '';
  const chip = (k: 'sellout' | 'sellin' | 'topup', text: string, title: string, empty: string) => (
    <span
      className={`chip${text ? '' : ' off'}`}
      style={{ '--chip': BF_SECTIONS[k].color, '--chip-bg': BF_SECTIONS[k].bg } as React.CSSProperties}
      title={`${BF_SECTIONS[k].label}${title ? `: ${title}` : ' — nothing agreed yet'}`}
    >
      <Icon name={BF_SECTIONS[k].icon} size={10} />
      {text || empty}
    </span>
  );
  return (
    <span className="deal">
      {chip('sellout', so, selloutLine(t), 'shoppers')}
      {chip('sellin', si, [sellinLine(t), num(t.order_amount) !== null ? `ordered ${fmtEurShort(t.order_amount)}` : ''].filter(Boolean).join(' · '), 'stock')}
      {chip('topup', tu, tu, 'top-up')}
    </span>
  );
};

/** Clickable column header: click to sort by it, click again to flip. */
const SortTh = ({ k, sort, desc, onClick, right, title, children }: {
  k: SortKey; sort: SortKey; desc: boolean; onClick: (k: SortKey) => void; right?: boolean; title?: string; children: React.ReactNode;
}) => {
  const active = sort === k;
  return (
    <th
      className={`sortable${active ? ' active' : ''}`}
      style={right ? { textAlign: 'right' } : undefined}
      title={title}
      aria-sort={active ? (desc ? 'descending' : 'ascending') : 'none'}
      onClick={() => onClick(k)}
    >
      <span className="th-in">
        {children}
        <span className="arrow">{active ? (desc ? '▼' : '▲') : '▲'}</span>
      </span>
    </th>
  );
};

const asksAnswered = (t: BrandTerms) => ASKS.some((a) => t.asks?.[a.key] && t.asks[a.key] !== 'open');

type SortKey = 'name' | 'status' | 'owner' | 'asks' | 'deal' | 'promo' | 'sales' | 'bf' | 'margin' | 'site';
const SORT_KEYS: SortKey[] = ['name', 'status', 'owner', 'asks', 'deal', 'promo', 'sales', 'bf', 'margin', 'site'];
/** Direction a column sorts in on the first click: text columns go A→Z,
 *  figures go biggest first. */
const SORT_DEFAULT_DESC: Record<SortKey, boolean> = {
  name: false, status: false, owner: false, asks: true, deal: true, promo: false, sales: true, bf: true, margin: true, site: true,
};

export const BFRegister = () => {
  const { year: yearParam } = useParams<{ year: string }>();
  const year = parseInt(yearParam || '', 10) || new Date().getFullYear();
  const navigate = useNavigate();
  const toast = useToast();
  const season = useBFSeason(year);
  const terms = useBFTerms(year);
  const brands = useBrands();
  const owners = useBFOwners();
  const seasonM = useSeasonMutations();
  const termsM = useTermsMutations();

  // Filters live in the URL so they survive opening a brand and coming back.
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const statusParam = params.get('status');
  const statusFilter = statusParam && STATUS_ORDER.includes(statusParam as BFStatus) ? (statusParam as BFStatus) : null;
  const owner = params.get('owner') ?? '';
  // "sort=deal" or "sort=deal:asc" — default direction per column, click again to flip.
  const [sortParamKey, sortParamDir] = (params.get('sort') ?? '').split(':');
  const sort: SortKey = SORT_KEYS.includes(sortParamKey as SortKey) ? (sortParamKey as SortKey) : 'sales';
  const desc = sortParamDir ? sortParamDir === 'desc' : SORT_DEFAULT_DESC[sort];
  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };
  const setQ = (v: string) => setParam('q', v || null);
  const setStatusFilter = (v: BFStatus | null) => setParam('status', v);
  const setOwner = (v: string) => setParam('owner', v || null);
  const clickSort = (k: SortKey) => {
    const nextDesc = k === sort ? !desc : SORT_DEFAULT_DESC[k];
    const isDefault = k === 'sales' && nextDesc;
    setParam('sort', isDefault ? null : (nextDesc === SORT_DEFAULT_DESC[k] ? k : `${k}:${nextDesc ? 'desc' : 'asc'}`));
  };
  const campaigns = useCampaigns();
  useEffect(() => rememberBFYear(year), [year]);
  const filterActive = !!(q.trim() || statusFilter || owner);
  const priority = useBFPriority(year, !!season.data);
  const pr = priority.data?.brands ?? {};

  const rows = useMemo(() => {
    let list = terms.data ?? [];
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((t) => t.brand_name.toLowerCase().includes(s) || t.contact.toLowerCase().includes(s));
    }
    if (statusFilter) list = list.filter((t) => t.status === statusFilter);
    if (owner === 'none') list = list.filter((t) => !t.owner);
    else if (owner) list = list.filter((t) => String(t.owner) === owner);
    // Every column sorts. Missing values always sink to the bottom, whatever
    // the direction, so "no figure" never floats above real figures.
    const nums: Record<Exclude<SortKey, 'name' | 'owner'>, (t: BrandTerms) => number | null> = {
      status: (t) => STATUS_ORDER.indexOf(t.status),
      asks: (t) => ASKS.filter((a) => t.asks?.[a.key] === 'yes').length,
      deal: (t) => num(t.discount_max_pct) ?? num(t.discount_recommended_pct) ?? num(t.our_discount_pct),
      promo: (t) => (t.promo_start || t.promo_end ? Date.parse((t.promo_start || t.promo_end) as string) : null),
      sales: (t) => pr[t.brand]?.sales_12m ?? null,
      bf: (t) => pr[t.brand]?.bf_sales ?? null,
      margin: (t) => pr[t.brand]?.gross_margin_pct_12m ?? null,
      site: (t) => pr[t.brand]?.discount_typical ?? null,
    };
    const texts: Partial<Record<SortKey, (t: BrandTerms) => string>> = {
      name: (t) => t.brand_name,
      owner: (t) => t.owner_name || '',
    };
    const dir = desc ? -1 : 1;
    const byName = (a: BrandTerms, b: BrandTerms) => a.brand_name.localeCompare(b.brand_name);
    const text = texts[sort];
    list = [...list].sort((a, b) => {
      if (text) {
        const x = text(a); const y = text(b);
        if (!x && y) return 1; if (x && !y) return -1;
        return dir * x.localeCompare(y) || byName(a, b);
      }
      const x = nums[sort as keyof typeof nums](a); const y = nums[sort as keyof typeof nums](b);
      if (x === null && y === null) return byName(a, b);
      if (x === null) return 1; if (y === null) return -1;
      return dir * (x - y) || byName(a, b);
    });
    return list;
  }, [terms.data, q, statusFilter, owner, sort, desc, pr]);

  const brandCount = Array.isArray(brands.data) ? brands.data.length : 0;
  const missing = Math.max(0, brandCount - (terms.data?.length ?? 0));

  const patchSeason = (patch: Record<string, string | null>, what = 'the season dates') =>
    seasonM.patch.mutate({ year, ...patch }, { onError: () => toast.push(`Could not save ${what}`) });
  const patchRow = (id: number, patch: TermsPatch, what: string) =>
    termsM.patch.mutate({ id, ...patch }, { onError: () => toast.push(`Could not save the ${what}`) });

  // Campaigns the season can hang its discounts on: this year's, newest first.
  const campaignOptions = useMemo(() => {
    const list = (campaigns.data ?? []).filter((c) => !c.parent && (!c.start_date || c.start_date.startsWith(String(year))));
    return [...list].sort((a, b) => (b.start_date ?? '').localeCompare(a.start_date ?? ''));
  }, [campaigns.data, year]);

  return (
    <div className="bf bf-wide">
      <style>{BF_STYLES}</style>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Black Friday</div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconButton aria-label="Previous season" onClick={() => navigate(`/black-friday/${year - 1}`)}><Icon name="chevronLeft" size={14} /></IconButton>
            Brands & terms {year}
            <IconButton aria-label="Next season" onClick={() => navigate(`/black-friday/${year + 1}`)}><Icon name="chevronRight" size={14} /></IconButton>
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
        {season.data?.campaign && (
          <Button variant="ghost" onClick={() => navigate(`/campaigns/${season.data!.campaign}`)}>
            🖤 {season.data.campaign_name || 'Black Week campaign'}
          </Button>
        )}
        {season.data && missing > 0 && (
          <Button
            variant="ghost"
            disabled={seasonM.populate.isPending}
            onClick={() => seasonM.populate.mutate(year, {
              onSuccess: (r) => toast.push(`${r.created} brands added as “To ask”`),
              onError: () => toast.push('Could not add the brands'),
            })}
          >
            <Icon name="plus" size={13} /> Add the {missing} other mosy brand{missing === 1 ? '' : 's'}
          </Button>
        )}
        </div>
      </div>

      {season.isLoading ? (
        <div className="empty">Loading…</div>
      ) : !season.data ? (
        <Card>
          <div className="empty" style={{ display: 'grid', gap: 12, justifyItems: 'start' }}>
            <div>There is no Black Friday {year} season yet.</div>
            <Button
              variant="primary"
              disabled={seasonM.create.isPending}
              onClick={() => seasonM.create.mutate({ year }, { onError: () => toast.push('Could not create the season') })}
            >
              <Icon name="plus" size={13} /> Start Black Friday {year}
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <Card style={{ marginBottom: 16 }}>
            <div className="season">
              <div>
                <div className="k">Our pre-Black Friday</div>
                <div className="dates">
                  <CommitInput type="date" value={season.data.prebf_start ?? ''} onCommit={(v) => patchSeason({ prebf_start: v || null })} />
                  <span className="muted">→</span>
                  <CommitInput type="date" value={season.data.prebf_end ?? ''} onCommit={(v) => patchSeason({ prebf_end: v || null })} />
                </div>
              </div>
              <div>
                <div className="k">Our Black Week</div>
                <div className="dates">
                  <CommitInput type="date" value={season.data.week_start ?? ''} onCommit={(v) => patchSeason({ week_start: v || null })} />
                  <span className="muted">→</span>
                  <CommitInput type="date" value={season.data.week_end ?? ''} onCommit={(v) => patchSeason({ week_end: v || null })} />
                </div>
              </div>
              <div>
                <div className="k">Placements go on</div>
                <div className="dates">
                  <select
                    className="input"
                    style={{ padding: '5px 8px', fontSize: 12.5, maxWidth: 220 }}
                    value={season.data.campaign ?? ''}
                    onChange={(e) => patchSeason({ campaign: e.target.value || null }, 'the campaign')}
                  >
                    <option value="">No campaign yet…</option>
                    {season.data.campaign && !campaignOptions.some((c) => c.id === season.data!.campaign) && (
                      <option value={season.data.campaign}>{season.data.campaign_name || season.data.campaign}</option>
                    )}
                    {campaignOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {season.data.campaign && (
                    <button type="button" className="link small" onClick={() => navigate(`/campaigns/${season.data!.campaign}`)}>Open</button>
                  )}
                </div>
              </div>
              <div>
                <div className="k">Progress</div>
                <div className="counts">
                  {STATUS_ORDER.filter((s) => season.data!.status_counts[s]).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`pill st-${s}`}
                      style={{ border: statusFilter === s ? '1.5px solid currentColor' : '1.5px solid transparent', cursor: 'pointer', font: 'inherit', fontSize: 11.5, fontWeight: 600 }}
                      onClick={() => setStatusFilter(statusFilter === s ? null : s)}
                    >
                      {season.data!.status_counts[s]} {STATUS_LABEL[s].toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          <div className="toolbar">
            <div className="search-wrap">
              <span className="search-icon"><Icon name="search" size={14} /></span>
              <input className="input" placeholder="Search brand or contact…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <select className="input" style={{ maxWidth: 200 }} value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Everyone</option>
              <option value="none">No owner</option>
              {(owners.data ?? []).map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
            </select>
            <span className="muted small" style={{ marginLeft: 'auto' }}>
              {rows.length} of {terms.data?.length ?? 0} brands
              {priority.data?.configured && !priority.data.error && priority.data.margins_through && ` · sales through ${fmtDay(priority.data.margins_through)}`}
              {priority.data && !priority.data.configured && ' · sales figures not connected yet'}
              {priority.data?.error && ' · sales figures unavailable right now'}
            </span>
          </div>
          <div className="filter-bar" style={{ margin: '0 0 12px' }}>
            <button type="button" className={`filter-pill${statusFilter ? '' : ' active'}`} onClick={() => setStatusFilter(null)}>
              All ({terms.data?.length ?? 0})
            </button>
            {STATUS_ORDER.map((s) => {
              const n = season.data!.status_counts[s] ?? 0;
              if (!n) return null;
              return (
                <button key={s} type="button" className={`filter-pill${statusFilter === s ? ' active' : ''}`} onClick={() => setStatusFilter(statusFilter === s ? null : s)}>
                  {STATUS_LABEL[s]} ({n})
                </button>
              );
            })}
          </div>

          <Card>
            {terms.isLoading ? (
              <div className="empty">Loading…</div>
            ) : (terms.data ?? []).length === 0 ? (
              <div className="empty">No brands on the {year} register yet. Use “Add the other mosy brands” to start.</div>
            ) : rows.length === 0 ? (
              <div className="empty" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span>
                  No brands match
                  {q.trim() ? <> “{q.trim()}”</> : ''}
                  {statusFilter ? <> in “{STATUS_LABEL[statusFilter]}”</> : ''}
                  {owner === 'none' ? ' without an owner' : owner ? ' for that owner' : ''}.
                </span>
                {filterActive && (
                  <button type="button" className="link small" onClick={() => setParams(new URLSearchParams(), { replace: true })}>Clear filters</button>
                )}
              </div>
            ) : (
              <div className="table-wrap">
                <table className="one-line">
                  <thead>
                    <tr>
                      <SortTh k="name" sort={sort} desc={desc} onClick={clickSort}>Brand</SortTh>
                      <SortTh k="status" sort={sort} desc={desc} onClick={clickSort}>Status</SortTh>
                      <SortTh k="owner" sort={sort} desc={desc} onClick={clickSort}>Owner</SortTh>
                      <SortTh k="asks" sort={sort} desc={desc} onClick={clickSort} title={`The six standard asks: ${ASKS.map((a) => a.label).join(' · ')}. Green = agreed, red = refused, grey = still open. Sorts by how many are agreed.`}>Asks</SortTh>
                      <SortTh k="deal" sort={sort} desc={desc} onClick={clickSort} title="Shopper discount · stock order · top-up. Greyed = not agreed (yet). Hover a chip for the details. Sorts by the shopper discount.">Deal</SortTh>
                      <SortTh k="promo" sort={sort} desc={desc} onClick={clickSort} title="The brand's own Black Friday campaign: from → until (brand page, Discount for shoppers). Sorts by start date.">Promo dates</SortTh>
                      <SortTh k="sales" sort={sort} desc={desc} onClick={clickSort} right title="Our revenue on this brand over the last 12 months">Sales 12 months</SortTh>
                      <SortTh k="bf" sort={sort} desc={desc} onClick={clickSort} right title={priority.data ? `Our revenue on this brand ${fmtDay(priority.data.bf_window.start_date)} – ${fmtDay(priority.data.bf_window.end_date)} ${priority.data.bf_year}` : 'Our revenue during last year’s Black Friday'}>Last Black Friday</SortTh>
                      <SortTh k="margin" sort={sort} desc={desc} onClick={clickSort} right title="Gross margin over the last 12 months (after shipping and payment fees, before ads)">Margin</SortTh>
                      <SortTh k="site" sort={sort} desc={desc} onClick={clickSort} title="Typical discount shoppers see on lamptwist.com right now (stock items separately)">On site now</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((t) => (
                      <tr key={t.id} className="clickable" onClick={() => navigate(`/black-friday/${year}/${t.brand}`)}>
                        <td>
                          <div className="brand-cell">
                            <BrandDot name={t.brand_name} logo={t.brand_logo} color={t.brand_color} />
                            <span className="brand-name">{t.brand_name}</span>
                            {t.visibility_conditions?.trim() && (
                              <span className="vis-mark" title={`Visibility conditions apply:\n${t.visibility_conditions.trim()}`}>
                                <Icon name="eye" size={13} />
                              </span>
                            )}
                          </div>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <select
                            className={`cell-select pill st-${t.status}`}
                            value={t.status}
                            onChange={(e) => patchRow(t.id, { status: e.target.value as BFStatus }, 'status')}
                          >
                            {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                          </select>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <select
                            className="cell-select"
                            value={t.owner ?? ''}
                            onChange={(e) => patchRow(t.id, { owner: e.target.value ? parseInt(e.target.value, 10) : null }, 'owner')}
                          >
                            <option value="">—</option>
                            {(owners.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </select>
                        </td>
                        <td>
                          {asksAnswered(t) ? (
                            <span className="asks" title={ASKS.map((a) => `${a.label}: ${({ yes: 'agreed', no: 'refused' } as Record<string, string>)[t.asks?.[a.key] ?? ''] ?? 'open'}`).join('\n')}>
                              {ASKS.map((a) => <span key={a.key} className={t.asks?.[a.key] ?? 'open'} />)}
                            </span>
                          ) : <span className="muted small">—</span>}
                        </td>
                        <td><DealChips t={t} /></td>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>{fmtRange(t.promo_start, t.promo_end) || <span className="muted">—</span>}</td>
                        <td className="small" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{pr[t.brand]?.sales_12m ? fmtEurShort(pr[t.brand].sales_12m) : <span className="muted">—</span>}</td>
                        <td className="small" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{pr[t.brand]?.bf_sales ? fmtEurShort(pr[t.brand].bf_sales) : <span className="muted">—</span>}</td>
                        <td className="small" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{pr[t.brand]?.gross_margin_pct_12m != null ? `${pr[t.brand].gross_margin_pct_12m}%` : <span className="muted">—</span>}</td>
                        <td className="small" style={{ whiteSpace: 'nowrap' }}>{siteDiscountLine(pr[t.brand]) || <span className="muted">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {rows.length > 0 && (
              <div className="legend">
                <span className="asks"><span className="yes" /></span> agreed
                <span className="asks"><span className="no" /></span> refused
                <span className="asks"><span /></span> still open
                <span className="muted">· the six asks: {ASKS.map((a) => a.label).join(', ')}</span>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default BFRegister;
